import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box, CircularProgress, Container, Grid, IconButton, Typography,
  Stack, Button, Chip, Divider, TextField, InputAdornment,
  MenuItem, FormControl, InputLabel, Select, Paper, Avatar,
  Tooltip, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import axios from 'axios';
import AuthService from '../Auth/AuthService';
import OfferCard from './OfferCard';
import ModalView from '../model/ModelView';
import AddSubscriptionPlan from './AddSubscriptionPlan';
import PageShell from '../sales/common/PageShell';
import StatCard from '../sales/common/StatCard';
import EmptyState from '../sales/common/EmptyState';

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userInfo] = useState(AuthService.tokenInfo());
  const [openForm, setOpenForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const fetchedRef = useRef(false);

  const isSuperAdmin = userInfo?.roles === 'SUPERADMIN';

  // ---------- Fetch ----------
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(AuthService.pathUrl('GET', '/subscription/plans'));
      const data = res?.data || [];

      // SUPERADMIN sees everything; other roles get school-type active plans only
      const visible = isSuperAdmin
        ? data
        : data.filter(
          (p) =>
            p.active &&
            (p.planType || '').toLowerCase().includes('school')
        );

      setPlans(visible);
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error.message);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchPlans();
  }, []);

  // ---------- Derived stats ----------
  const stats = useMemo(() => {
    const total = plans.length;
    const active = plans.filter((p) => p.active).length;
    const premium = plans.filter((p) =>
      (p.name || '').toLowerCase().includes('premium')
    ).length;
    const cheapest = plans.reduce(
      (min, p) => (p.price != null && (min == null || p.price < min) ? p.price : min),
      null
    );
    return { total, active, premium, cheapest };
  }, [plans]);

  // ---------- Filtered list ----------
  const filteredPlans = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return plans.filter((plan) => {
      if (statusFilter === 'active' && !plan.active) return false;
      if (statusFilter === 'inactive' && plan.active) return false;
      if (!term) return true;
      return [plan.name, plan.description, plan.planType]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [plans, searchTerm, statusFilter]);

  // ---------- Handlers ----------
  const handleOpen = () => setOpenForm(true);
  const handleClose = () => setOpenForm(false);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all';

  // ---------- Loading ----------
  if (loading && plans.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageShell
      icon={<WorkspacePremiumIcon />}
      title="Subscription Plans"
      subtitle={
        isSuperAdmin
          ? 'Manage all subscription plans available to your users'
          : 'Choose a plan that fits your needs'
      }
      breadcrumbs={[
        { label: 'Dashboard', to: '/' },
        { label: 'Subscription Plans' },
      ]}
      actions={
        <>
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchPlans}
              disabled={loading}
              sx={{
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.5)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {isSuperAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpen}
              sx={{
                bgcolor: '#fff',
                color: '#1e3a8a',
                fontWeight: 700,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
              }}
            >
              Add Plan
            </Button>
          )}
        </>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total Plans" value={stats.total} icon={<WorkspacePremiumIcon />} color="#2563eb" />
        <StatCard label="Active" value={stats.active} icon={<CheckCircleIcon />} color="#059669" />
        <StatCard label="Premium" value={stats.premium} icon={<StarIcon />} color="#ea580c" />
        <StatCard
          label="Starting From"
          value={stats.cheapest != null ? `₹${stats.cheapest}` : '—'}
          icon={<TrendingUpIcon />}
          color="#7c3aed"
        />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          size="small"
          placeholder="Search plans by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All Plans</MenuItem>
            <MenuItem value="active">Active only</MenuItem>
            <MenuItem value="inactive">Inactive only</MenuItem>
          </Select>
        </FormControl>

        {hasActiveFilters && (
          <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>
            Clear
          </Button>
        )}
      </Paper>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing <strong>{filteredPlans.length}</strong> of{' '}
        <strong>{plans.length}</strong> {plans.length === 1 ? 'plan' : 'plans'}
        {searchTerm && ` matching "${searchTerm}"`}
      </Typography>

      {/* Grid / Empty */}
      {filteredPlans.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<LocalOfferIcon />}
            title={plans.length === 0 ? 'No plans available' : 'No matches'}
            subtitle={
              plans.length === 0
                ? isSuperAdmin
                  ? 'Create your first subscription plan to get started.'
                  : 'No plans are available for your account yet.'
                : 'Try a different search term or clear the filters.'
            }
            actionLabel={
              plans.length === 0 && isSuperAdmin
                ? 'Add Plan'
                : hasActiveFilters
                  ? 'Clear Filters'
                  : undefined
            }
            onAction={
              plans.length === 0 && isSuperAdmin ? handleOpen : clearFilters
            }
          />
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={plan.id}>
              <OfferCard
                plan={plan}
                userInfo={userInfo}
                onSuccess={fetchPlans}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Plan dialog */}
      <ModalView
        open={openForm}
        handleClose={handleClose}
        title="Add Subscription Plan"
        content={
          <AddSubscriptionPlan
            onSuccess={() => {
              setOpenForm(false);
              fetchPlans();
            }}
          />
        }
      />
    </PageShell>
  );
};

export default SubscriptionPlans;